<div class="onboarding-page min-h-screen bg-white font-inter text-slate-950">
  <div class="grid min-h-screen lg:grid-cols-[2fr_3fr]">

    <!-- Left: Dark branding panel -->
    <div class="auth-brand-panel relative hidden overflow-hidden bg-slate-950 lg:flex lg:flex-col">
      <div class="pointer-events-none absolute inset-0 bg-gradient-to-br from-black/80 via-black/75 to-black/90"></div>
      <div class="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-primary-600/10 blur-3xl"></div>
      <div class="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-primary-400/10 blur-3xl"></div>

      <div class="relative flex h-full flex-col justify-between px-10 py-12 xl:px-16">

        <a routerLink="/" class="inline-flex items-center gap-3">
          <img
            src="assets/brand/logo.svg"
            width="40"
            height="40"
            alt="FormOps logo"
            class="h-10 w-10 shrink-0 rounded-2xl bg-white p-1.5 shadow-lg shadow-black/20" />
          <div>
            <p class="text-h6 text-white">FormOps</p>
            <p class="text-caption text-slate-400">AI Workflow Platform</p>
          </div>
        </a>

        <div class="flex flex-col gap-4">
          <p class="text-body3 font-medium uppercase tracking-widest text-primary-400">Get Started</p>
          <h1 class="text-h2 text-white">Welcome!</h1>
          <p class="text-body1 text-slate-400">
            Explore our intuitive dashboard and set up your account in just a few clicks.
          </p>
        </div>

        <div class="flex flex-col gap-8">
          <blockquote class="text-body2 italic leading-relaxed text-slate-300">
            "Great system — saves loads of time building forms and automating workflows. Highly recommended!"
          </blockquote>
          <div class="flex items-center gap-3">
            <div
              class="flex h-10 w-10 items-center justify-center rounded-full bg-primary-600/30 text-body3 font-semibold text-primary-300">
              JD
            </div>
            <div>
              <p class="text-body2 font-medium text-white">Jane Doe</p>
              <p class="text-body3 text-slate-400">Product Manager</p>
            </div>
          </div>

          <div class="flex flex-col gap-3">
            <div class="flex gap-6 text-body3 text-slate-500">
              <a routerLink="/" class="transition-colors hover:text-slate-300">Terms</a>
              <a routerLink="/" class="transition-colors hover:text-slate-300">Privacy Policy</a>
              <a routerLink="/login" class="transition-colors hover:text-slate-300">Sign in</a>
            </div>
            <p class="text-caption text-slate-600">© {{ currentYear }} FormOps. All rights reserved.</p>
          </div>
        </div>

      </div>
    </div>

    <!-- Right: Onboarding steps panel -->
    <div class="flex flex-col justify-center bg-white px-6 py-12 sm:px-10 lg:px-16 xl:px-24">
      <div class="mx-auto flex w-full max-w-lg flex-col gap-10">

        <div class="flex items-center gap-2">
          @for (step of [1, 2, 3, 4]; track step) {
            <div
              [class]="step === currentStep()
                ? 'h-2 w-8 rounded-full bg-primary-600'
                : 'h-2 w-2 rounded-full bg-slate-200'">
            </div>
          }
        </div>

        <div class="flex flex-col gap-8">
          <!-- Step 1: Industry -->
          @if (currentStep() === 1) {
            <div class="flex flex-col gap-8">
              <div class="flex flex-col gap-2">
                <h2 class="text-h3 text-slate-950">What industry are you in?</h2>
                <p class="text-body1 text-slate-500">Help us tailor FormOps to your needs.</p>
              </div>
              <div class="flex flex-col gap-3">
                @for (option of industryOptions; track option) {
                  <div
                    (click)="selectedIndustry.set(option)"
                    [class]="selectedIndustry() === option
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-slate-200 text-slate-700 hover:border-primary-300'"
                    class="cursor-pointer select-none rounded-xl border px-5 py-3.5 text-body2 transition-colors">
                    {{ option }}
                  </div>
                }
              </div>
            </div>
          }

          <!-- Step 2: Planned use cases (multi-select) -->
          @if (currentStep() === 2) {
            <div class="flex flex-col gap-8">
              <div class="flex flex-col gap-2">
                <h2 class="text-h3 text-slate-950">What do you plan to use FormOps for?</h2>
                <p class="text-body1 text-slate-500">Select all that apply.</p>
              </div>
              <div class="flex flex-col gap-3">
                @for (option of useCaseOptions; track option) {
                  <div
                    (click)="toggleUseCase(option)"
                    [class]="isUseCaseSelected(option)
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-slate-200 text-slate-700 hover:border-primary-300'"
                    class="cursor-pointer select-none rounded-xl border px-5 py-3.5 text-body2 transition-colors">
                    {{ option }}
                  </div>
                }
              </div>
            </div>
          }

          <!-- Step 3: Team size -->
          @if (currentStep() === 3) {
            <div class="flex flex-col gap-8">
              <div class="flex flex-col gap-2">
                <h2 class="text-h3 text-slate-950">How large is your team?</h2>
                <p class="text-body1 text-slate-500">This helps us recommend the right plan for you.</p>
              </div>
              <div class="flex flex-col gap-3">
                @for (option of teamSizeOptions; track option) {
                  <div
                    (click)="selectedTeamSize.set(option)"
                    [class]="selectedTeamSize() === option
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-slate-200 text-slate-700 hover:border-primary-300'"
                    class="cursor-pointer select-none rounded-xl border px-5 py-3.5 text-body2 transition-colors">
                    {{ option }}
                  </div>
                }
              </div>
            </div>
          }

          <!-- Step 4: How did you hear about us -->
          @if (currentStep() === 4) {
            <div class="flex flex-col gap-8">
              <div class="flex flex-col gap-2">
                <h2 class="text-h3 text-slate-950">How did you hear about us?</h2>
                <p class="text-body1 text-slate-500">We'd love to know where you found us.</p>
              </div>
              <div class="flex flex-col gap-3">
                @for (option of heardAboutOptions; track option) {
                  <div
                    (click)="selectedHeardAbout.set(option)"
                    [class]="selectedHeardAbout() === option
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-slate-200 text-slate-700 hover:border-primary-300'"
                    class="cursor-pointer select-none rounded-xl border px-5 py-3.5 text-body2 transition-colors">
                    {{ option }}
                  </div>
                }
              </div>
            </div>
          }

          <div class="flex items-center justify-between gap-3">
            <lib-signal-button
              type="button"
              appearance="text"
              [loading]="skipState.loading()"
              [disabled]="isLoading()"
              [fullWidth]="false"
              (clicked)="onSkip()">
              Skip
            </lib-signal-button>
            <div class="flex items-center gap-3">
              @if (currentStep() > 1) {
                <lib-signal-button
                  type="button"
                  appearance="outline"
                  [fullWidth]="false"
                  [disabled]="isLoading()"
                  (clicked)="goBack()">
                  Back
                </lib-signal-button>
              }
              <lib-signal-button
                type="button"
                [loading]="isLoading()"
                [disabled]="isLoading() || !canContinue()"
                [fullWidth]="false"
                (clicked)="onContinue()">
                {{ currentStep() === 4 ? 'Finish' : 'Continue' }}
              </lib-signal-button>
            </div>
          </div>
        </div>

      </div>
    </div>

  </div>
</div>
